import {
  Alert,
  Badge,
  Button,
  Card,
  CopyButton,
  Divider,
  Group,
  Loader,
  Modal,
  ScrollArea,
  SegmentedControl,
  Stack,
  Text,
} from "@mantine/core";
import { type IBreeamCriterionCheckOutput } from "@mcapps/shared";
import { IconAlertCircle, IconCheck, IconCopy, IconSparkles } from "@tabler/icons-react";
import React, { useEffect, useRef, useState } from "react";
import { usePostProjectByProjectIdIssuesAndIssueIdRunBreeamCheckMutation } from "../../../../../../../openapi";
import { useAppSelector } from "../../../../../../hooks";
import { selectCurrentOpenIssueInModal } from "../../../issueSelectors";
import {
  parseAiAssessment,
  selectBreeamModalHeader,
  selectBreeamRunArgs,
  selectVerdictColor,
} from "./IssueBreeamCriterionCheckModal.selectors";

const getErrorMessage = (error: unknown) => {
  if (error == null) return "Ukjent feil";
  if (typeof error === "string") return error;
  if (typeof error === "object" && "data" in error) {
    const data = (error as { data?: unknown }).data;
    if (typeof data === "string") return data;
    return JSON.stringify(data);
  }
  return JSON.stringify(error);
};

const formatNumber = (value: number) => new Intl.NumberFormat("nb-NO").format(value);

const formatNok = (value: number) =>
  new Intl.NumberFormat("nb-NO", {
    style: "currency",
    currency: "NOK",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);

const formatDateTime = (value?: string | Date | null) => {
  if (value == null) return "";

  const date = value instanceof Date ? value : new Date(value);

  if (Number.isNaN(date.getTime())) return "";

  return new Intl.DateTimeFormat("nb-NO", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
};

const TextPreviewCard = ({ title, text }: { title: string; text: string }) => (
  <Card withBorder radius="md" padding="sm">
    <Stack gap={6}>
      <Group justify="space-between" align="center">
        <Text fw={600} size="sm">
          {title}
        </Text>
        <Badge variant="light" color={text.trim().length > 0 ? "teal" : "gray"}>
          {formatNumber(text.length)} tegn
        </Badge>
      </Group>
      <Divider />
      <Text size="sm" c={text.trim().length > 0 ? undefined : "dimmed"} style={{ whiteSpace: "pre-wrap" }}>
        {text.trim() || "Tomt felt"}
      </Text>
    </Stack>
  </Card>
);

type TBreeamOutputLanguage = "NO" | "EN";

type TSavedBreeamCriterionCheck = NonNullable<
  NonNullable<ReturnType<typeof selectCurrentOpenIssueInModal>>["aiResults"]
>["breeamCriterionCheck"];

const KeywordResult = ({ result }: { result: IBreeamCriterionCheckOutput }) => {
  const keywordExtraction = result.inputData.aiInput.keywordExtraction;
  const { tokenEstimate } = keywordExtraction;

  return (
    <Stack gap="md">
      <Card withBorder radius="md" padding="md">
        <Stack gap="sm">
          <Group justify="space-between" align="center">
            <Text fw={700}>AI-valgte søkord</Text>
            <Group gap={6}>
              {keywordExtraction.fallbackUsed && (
                <Badge variant="light" color="yellow">
                  Fallback brukt
                </Badge>
              )}
              <Badge variant="light" color="blue">
                {result.inputData.aiInput.relevanceKeywords.length} søkord
              </Badge>
            </Group>
          </Group>
          <Group gap={8}>
            {result.inputData.aiInput.relevanceKeywords.map((keyword) => (
              <Badge key={keyword} variant="light" color="teal" size="lg">
                {keyword}
              </Badge>
            ))}
          </Group>
          {keywordExtraction.error != null && (
            <Alert color="yellow" variant="light" icon={<IconAlertCircle size={18} />} title="AI-uttrekk feilet">
              <Text size="sm">{keywordExtraction.error}</Text>
            </Alert>
          )}
        </Stack>
      </Card>

      <Card withBorder radius="md" padding="sm">
        <Stack gap={6}>
          <Text fw={600} size="sm">
            Kostnadsestimat for søkeord-uttrekk
          </Text>
          <Divider />
          <Group gap={8}>
            <Badge variant="light" color="blue">
              ca. {formatNumber(tokenEstimate.totalTokens)} tokens
            </Badge>
            <Badge variant="light" color="blue">
              ca. {formatNok(tokenEstimate.estimatedCostNok)}
            </Badge>
          </Group>
          <Text size="xs" c="dimmed">
            {tokenEstimate.pricingNote}
          </Text>
        </Stack>
      </Card>

      <TextPreviewCard title="Dokumentasjon fra attributt C" text={result.inputData.documentationText} />
      <TextPreviewCard title="Veiledning fra attributt I" text={result.inputData.guidanceText} />
      <TextPreviewCard title="Samlet tekst sendt til søkeord-AI" text={result.inputData.combinedCriterionText} />

      <Card withBorder radius="md" padding="sm">
        <Stack gap={6}>
          <Group justify="space-between" align="center">
            <Text fw={600} size="sm">
              Rått AI-svar
            </Text>
            <CopyButton value={keywordExtraction.rawAiText}>
              {({ copied, copy }) => (
                <Button size="xs" variant="subtle" leftSection={<IconCopy size={14} />} onClick={copy}>
                  {copied ? "Kopiert" : "Kopier"}
                </Button>
              )}
            </CopyButton>
          </Group>
          <Divider />
          <Text size="xs" style={{ whiteSpace: "pre-wrap" }}>
            {keywordExtraction.rawAiText || "Ingen råtekst. Fallback-listen ble brukt."}
          </Text>
        </Stack>
      </Card>

      <Card withBorder radius="md" padding="sm">
        <Stack gap={6}>
          <Group justify="space-between" align="center">
            <Text fw={600} size="sm">
              Prompt for vurdering med PDF-data
            </Text>
            <CopyButton value={result.inputData.aiInput.prompt}>
              {({ copied, copy }) => (
                <Button size="xs" variant="subtle" leftSection={<IconCopy size={14} />} onClick={copy}>
                  {copied ? "Kopiert" : "Kopier"}
                </Button>
              )}
            </CopyButton>
          </Group>
          <Divider />
          <Text size="xs" style={{ whiteSpace: "pre-wrap" }}>
            {result.inputData.aiInput.prompt}
          </Text>
        </Stack>
      </Card>

      <Card withBorder radius="md" padding="sm">
        <Stack gap={6}>
          <Group justify="space-between" align="center">
            <Text fw={600} size="sm">
              Prompt for søkeord
            </Text>
            <CopyButton value={keywordExtraction.prompt}>
              {({ copied, copy }) => (
                <Button size="xs" variant="subtle" leftSection={<IconCopy size={14} />} onClick={copy}>
                  {copied ? "Kopiert" : "Kopier"}
                </Button>
              )}
            </CopyButton>
          </Group>
          <Divider />
          <Text size="xs" style={{ whiteSpace: "pre-wrap" }}>
            {keywordExtraction.prompt}
          </Text>
        </Stack>
      </Card>
    </Stack>
  );
};

const AnalysisResult = ({ result }: { result: IBreeamCriterionCheckOutput }) => {
  const rawAnalysisText = result.analysis[0] ?? "";
  const parsedAssessment = parseAiAssessment(rawAnalysisText);
  const verdict = parsedAssessment.verdict ?? result.verdict;
  const verdictColor = selectVerdictColor(verdict);

  return (
    <Card withBorder radius="md" padding="md">
      <Stack gap="md">
        <Group justify="space-between" align="center">
          <Group gap="xs">
            <IconCheck size={18} color={`var(--mantine-color-${verdictColor}-6)`} />
            <Text fw={700}>AI-vurdering</Text>
          </Group>
          <Group gap="xs">
            <Badge variant="light" color={verdictColor} size="lg">
              {verdict}
            </Badge>
            <CopyButton value={parsedAssessment.breText || rawAnalysisText}>
              {({ copied, copy }) => (
                <Button
                  size="sm"
                  variant={copied ? "filled" : "light"}
                  color={copied ? "teal" : "blue"}
                  leftSection={copied ? <IconCheck size={16} /> : <IconCopy size={16} />}
                  onClick={copy}
                >
                  {copied ? "Tekst kopiert" : "Kopier tekst til BRE"}
                </Button>
              )}
            </CopyButton>
          </Group>
        </Group>

        {parsedAssessment.summary.trim().length > 0 && <Text size="sm">{parsedAssessment.summary}</Text>}

        <Card withBorder radius="sm" padding="sm">
          <Stack gap={6}>
            <Text fw={600} size="sm">
              Forslag til tekst til BRE
            </Text>
            <Divider />
            <Text size="sm" style={{ whiteSpace: "pre-wrap" }}>
              {parsedAssessment.breText || "Ingen tekst til BRE funnet i AI-svaret."}
            </Text>
          </Stack>
        </Card>

        <Card withBorder radius="sm" padding="sm">
          <Stack gap={6}>
            <Text fw={600} size="sm">
              Dokumentasjon brukt
            </Text>
            <Divider />
            {parsedAssessment.supportingPoints.length > 0 ? (
              <Stack gap="xs">
                {parsedAssessment.supportingPoints.map((point, index) => (
                  <Text key={`${index}-${point.slice(0, 20)}`} size="sm">
                    - {point}
                  </Text>
                ))}
              </Stack>
            ) : (
              <Text size="sm" c="dimmed">
                Ingen dokumentasjonspunkter funnet i AI-svaret.
              </Text>
            )}
          </Stack>
        </Card>

        <Card withBorder radius="sm" padding="sm">
          <Stack gap={6}>
            <Text fw={600} size="sm">
              Gap / usikkerheter
            </Text>
            <Divider />
            {parsedAssessment.gaps.length > 0 ? (
              <Stack gap="xs">
                {parsedAssessment.gaps.map((gap, index) => (
                  <Text key={`${index}-${gap.slice(0, 20)}`} size="sm">
                    - {gap}
                  </Text>
                ))}
              </Stack>
            ) : (
              <Text size="sm" c="dimmed">
                Ingen gap funnet i AI-svaret.
              </Text>
            )}
          </Stack>
        </Card>
      </Stack>
    </Card>
  );
};

const SavedAnalysisResult = ({ savedResult }: { savedResult: NonNullable<TSavedBreeamCriterionCheck> }) => {
  const verdictColor = selectVerdictColor(savedResult.verdict);
  const createdAtText = formatDateTime(savedResult.createdAt);

  return (
    <Card withBorder radius="md" padding="md">
      <Stack gap="md">
        <Group justify="space-between" align="center">
          <Group gap="xs">
            <IconCheck size={18} color={`var(--mantine-color-${verdictColor}-6)`} />
            <Stack gap={0}>
              <Text fw={700}>Lagret AI-vurdering</Text>
              <Text size="xs" c="dimmed">
                {[savedResult.createdBy?.name != null ? `Kjørt av ${savedResult.createdBy.name}` : "", createdAtText]
                  .filter(Boolean)
                  .join(" · ")}
              </Text>
            </Stack>
          </Group>
          <Group gap="xs">
            <Badge variant="light" color={verdictColor} size="lg">
              {savedResult.verdict}
            </Badge>
            <CopyButton value={savedResult.output.breText || savedResult.output.rawAnalysisText}>
              {({ copied, copy }) => (
                <Button
                  size="sm"
                  variant={copied ? "filled" : "light"}
                  color={copied ? "teal" : "blue"}
                  leftSection={copied ? <IconCheck size={16} /> : <IconCopy size={16} />}
                  onClick={copy}
                >
                  {copied ? "Tekst kopiert" : "Kopier tekst til BRE"}
                </Button>
              )}
            </CopyButton>
          </Group>
        </Group>

        {savedResult.summary.trim().length > 0 && <Text size="sm">{savedResult.summary}</Text>}

        <Card withBorder radius="sm" padding="sm">
          <Stack gap={6}>
            <Text fw={600} size="sm">
              Forslag til tekst til BRE
            </Text>
            <Divider />
            <Text size="sm" style={{ whiteSpace: "pre-wrap" }}>
              {savedResult.output.breText || "Ingen tekst til BRE funnet i lagret AI-svar."}
            </Text>
          </Stack>
        </Card>

        <Group gap={8}>
          {savedResult.output.keywords.map((keyword) => (
            <Badge key={keyword} variant="light" color="teal">
              {keyword}
            </Badge>
          ))}
        </Group>
      </Stack>
    </Card>
  );
};

const SavedInputDataResult = ({ savedResult }: { savedResult: NonNullable<TSavedBreeamCriterionCheck> }) => (
  <Stack gap="md">
    {savedResult.usage != null && (
      <Card withBorder radius="md" padding="sm">
        <Stack gap={6}>
          <Text fw={600} size="sm">
            Lagret token- og kostnadsestimat
          </Text>
          <Divider />
          <Group gap={8}>
            {savedResult.usage.totalTokens != null && (
              <Badge variant="light" color="blue">
                ca. {formatNumber(savedResult.usage.totalTokens)} tokens totalt
              </Badge>
            )}
            {savedResult.usage.estimatedCostNok != null && (
              <Badge variant="light" color="blue">
                ca. {formatNok(savedResult.usage.estimatedCostNok)}
              </Badge>
            )}
            {savedResult.usage.model != null && (
              <Badge variant="light" color="gray">
                {savedResult.usage.model}
              </Badge>
            )}
          </Group>
        </Stack>
      </Card>
    )}

    <TextPreviewCard title="Dokumentasjon fra attributt C" text={savedResult.inputSnapshot.documentationText} />
    <TextPreviewCard title="Veiledning fra attributt I" text={savedResult.inputSnapshot.guidanceText} />
    <TextPreviewCard title="Samlet kriterietekst" text={savedResult.inputSnapshot.combinedCriterionText} />

    <Card withBorder radius="md" padding="sm">
      <Stack gap={6}>
        <Text fw={600} size="sm">
          Kilder
        </Text>
        <Divider />
        <Stack gap="xs">
          {savedResult.sourceFiles.map((file) => (
            <Group key={`${file.blobName}-${file.fileName}`} justify="space-between" align="center">
              <Text size="sm">{file.fileName}</Text>
              <Badge variant="light" color={file.included ? "teal" : "gray"}>
                {file.included ? "Brukt" : "Ikke brukt"}
              </Badge>
            </Group>
          ))}
        </Stack>
      </Stack>
    </Card>

    <Card withBorder radius="md" padding="sm">
      <Stack gap={6}>
        <Group justify="space-between" align="center">
          <Text fw={600} size="sm">
            Prompt for vurdering med PDF-data
          </Text>
          <CopyButton value={savedResult.inputSnapshot.assessmentPrompt}>
            {({ copied, copy }) => (
              <Button size="xs" variant="subtle" leftSection={<IconCopy size={14} />} onClick={copy}>
                {copied ? "Kopiert" : "Kopier"}
              </Button>
            )}
          </CopyButton>
        </Group>
        <Divider />
        <Text size="xs" style={{ whiteSpace: "pre-wrap" }}>
          {savedResult.inputSnapshot.assessmentPrompt}
        </Text>
      </Stack>
    </Card>

    <Card withBorder radius="md" padding="sm">
      <Stack gap={6}>
        <Group justify="space-between" align="center">
          <Text fw={600} size="sm">
            Prompt for søkeord
          </Text>
          <CopyButton value={savedResult.inputSnapshot.keywordPrompt}>
            {({ copied, copy }) => (
              <Button size="xs" variant="subtle" leftSection={<IconCopy size={14} />} onClick={copy}>
                {copied ? "Kopiert" : "Kopier"}
              </Button>
            )}
          </CopyButton>
        </Group>
        <Divider />
        <Text size="xs" style={{ whiteSpace: "pre-wrap" }}>
          {savedResult.inputSnapshot.keywordPrompt}
        </Text>
      </Stack>
    </Card>
  </Stack>
);

const InputDataResult = ({ result }: { result: IBreeamCriterionCheckOutput }) => {
  const rawAnalysisText = result.analysis[0] ?? "";
  const keywordEstimate = result.inputData.aiInput.keywordExtraction.tokenEstimate;
  const assessmentEstimate = result.inputData.aiInput.tokenEstimate;
  const totalTokens = keywordEstimate.totalTokens + assessmentEstimate.totalTokens;
  const totalCostNok = keywordEstimate.estimatedCostNok + assessmentEstimate.estimatedCostNok;

  return (
    <Stack gap="md">
      <Card withBorder radius="md" padding="sm">
        <Stack gap={6}>
          <Text fw={600} size="sm">
            Totalt token- og kostnadsestimat
          </Text>
          <Divider />
          <Group gap={8}>
            <Badge variant="light" color="blue">
              ca. {formatNumber(totalTokens)} tokens totalt
            </Badge>
            <Badge variant="light" color="blue">
              ca. {formatNok(totalCostNok)}
            </Badge>
            <Badge variant="light" color="gray">
              søkeord: {formatNumber(keywordEstimate.totalTokens)}
            </Badge>
            <Badge variant="light" color="gray">
              vurdering: {formatNumber(assessmentEstimate.totalTokens)}
            </Badge>
          </Group>
        </Stack>
      </Card>

      <KeywordResult result={result} />

      <Card withBorder radius="md" padding="sm">
        <Stack gap={6}>
          <Group justify="space-between" align="center">
            <Text fw={600} size="sm">
              PDF-tekst sendt til AI
            </Text>
            <Badge variant="light" color="teal">
              {result.inputData.aiInput.documents.length} dokumenter
            </Badge>
          </Group>
          <Divider />
          <Stack gap="xs">
            {result.inputData.aiInput.documents.map((document) => (
              <Stack key={document.fileName} gap={2}>
                <Group justify="space-between" align="center">
                  <Text fw={600} size="sm">
                    {document.fileName}
                  </Text>
                  <Badge variant="light" color="blue">
                    {formatNumber(document.selectedTextLength)} / {formatNumber(document.totalTextLength)} tegn
                  </Badge>
                </Group>
                {document.pages.length > 0 && (
                  <Text size="xs" c="dimmed">
                    Valgte sider:{" "}
                    {document.pages
                      .filter((page) => page.selected)
                      .map((page) => page.pageNumber)
                      .join(", ") || "Ingen"}
                  </Text>
                )}
              </Stack>
            ))}
          </Stack>
        </Stack>
      </Card>

      <Card withBorder radius="md" padding="sm">
        <Stack gap={6}>
          <Group justify="space-between" align="center">
            <Text fw={600} size="sm">
              Rå AI-vurdering
            </Text>
            <CopyButton value={rawAnalysisText}>
              {({ copied, copy }) => (
                <Button size="xs" variant="subtle" leftSection={<IconCopy size={14} />} onClick={copy}>
                  {copied ? "Kopiert" : "Kopier"}
                </Button>
              )}
            </CopyButton>
          </Group>
          <Divider />
          <Text size="xs" style={{ whiteSpace: "pre-wrap" }}>
            {rawAnalysisText}
          </Text>
        </Stack>
      </Card>
    </Stack>
  );
};

const IssueBreeamCriterionCheckModal: React.FC<{
  onClose: () => void;
}> = ({ onClose }) => {
  const issue = useAppSelector(selectCurrentOpenIssueInModal);
  const { issueId, issueTitle } = useAppSelector(selectBreeamModalHeader);
  const breeamRunArgs = useAppSelector(selectBreeamRunArgs);
  const hasLoadedInputPreviewRef = useRef(false);
  const [outputLanguage, setOutputLanguage] = useState<TBreeamOutputLanguage>("EN");
  const [showInputData, setShowInputData] = useState(false);
  const [runBreeamCriterionCheck, { data: result = null, error, isLoading, reset }] =
    usePostProjectByProjectIdIssuesAndIssueIdRunBreeamCheckMutation();
  const savedResult = issue?.aiResults?.breeamCriterionCheck ?? null;
  const keywordEstimate = result?.inputData.aiInput.keywordExtraction.tokenEstimate;
  const assessmentEstimate = result?.inputData.aiInput.tokenEstimate;
  const hasAiResult = result?.status === "success";
  const hasSavedResult = savedResult != null && result == null;
  const hasCompletedAiResult = hasAiResult || hasSavedResult;
  const shouldShowRunAnalysisCard = !hasCompletedAiResult;
  const totalTokenEstimate =
    keywordEstimate != null && assessmentEstimate != null
      ? {
          totalTokens: keywordEstimate.totalTokens + assessmentEstimate.totalTokens,
          estimatedCostNok: keywordEstimate.estimatedCostNok + assessmentEstimate.estimatedCostNok,
        }
      : null;

  const runCheck = (runAi: boolean) => {
    if (breeamRunArgs == null) {
      return;
    }

    if (runAi) {
      reset();
    }

    void runBreeamCriterionCheck({
      projectId: breeamRunArgs.projectId,
      issueId: breeamRunArgs.issueId,
      body: {
        body: {
          runAi,
          outputLanguage,
        },
      },
    });
  };

  useEffect(() => {
    if (breeamRunArgs == null || hasLoadedInputPreviewRef.current) {
      return;
    }

    if (savedResult != null) {
      return;
    }

    hasLoadedInputPreviewRef.current = true;
    runCheck(false);
  }, [breeamRunArgs, savedResult]);

  return (
    <Modal
      opened
      onClose={onClose}
      size="70vw"
      centered
      radius="lg"
      styles={{
        content: {
          maxWidth: "70rem",
          height: "84vh",
        },
        body: {
          height: "100%",
        },
      }}
      title={
        <Group gap="xs">
          <IconSparkles size={18} />
          <Text fw={600}>BREEAM-sjekk for sak {issueId}</Text>
        </Group>
      }
    >
      <Stack h="100%" gap="md">
        <Group justify="space-between" align="flex-start">
          <Stack gap={2}>
            <Text fw={600}>{issueTitle}</Text>
          </Stack>
          {hasCompletedAiResult && (
            <Button
              size="sm"
              variant="light"
              leftSection={<IconSparkles size={16} />}
              loading={isLoading}
              disabled={breeamRunArgs == null}
              onClick={() => runCheck(true)}
            >
              Kjør på nytt
            </Button>
          )}
        </Group>

        {shouldShowRunAnalysisCard && (
          <Card
            withBorder
            radius="md"
            padding="lg"
            style={{
              backgroundColor: "#e7f5ff",
              borderColor: "#228be6",
            }}
          >
            <Stack gap="md" align="center">
              <Stack gap={4} align="center">
                <Text fw={700} size="lg" ta="center">
                  Kjør AI-analyse
                </Text>
                <Text size="sm" c="dimmed" ta="center">
                  {totalTokenEstimate != null
                    ? `Estimert totalt: ca. ${formatNumber(totalTokenEstimate.totalTokens)} tokens / ${formatNok(
                        totalTokenEstimate.estimatedCostNok,
                      )}`
                    : "Henter token- og kostnadsestimat..."}
                </Text>
              </Stack>

              <SegmentedControl
                value={outputLanguage}
                onChange={(value) => setOutputLanguage(value as TBreeamOutputLanguage)}
                data={[
                  { label: "English", value: "EN" },
                  { label: "Norsk", value: "NO" },
                ]}
              />
              <Text size="xs" c="dimmed" ta="center">
                Språkvalget styrer teksten som genereres til BRE Online. English er valgt som standard.
              </Text>

              <Button
                size="lg"
                variant="filled"
                leftSection={<IconSparkles size={20} />}
                loading={isLoading}
                disabled={breeamRunArgs == null || totalTokenEstimate == null}
                onClick={() => runCheck(true)}
                miw={260}
                mt="xs"
                styles={{
                  root: {
                    backgroundColor: "#1864ab",
                    borderColor: "#1864ab",
                    color: "#fff",
                    display: "inline-flex",
                    fontSize: "1.05rem",
                    fontWeight: 700,
                    minHeight: 52,
                  },
                  section: {
                    color: "#fff",
                  },
                }}
              >
                Kjør AI-analyse
              </Button>

              {totalTokenEstimate != null && keywordEstimate != null && assessmentEstimate != null && (
                <Group gap={6} mt={4}>
                  <Badge variant="white" color="blue">
                    ca. {formatNumber(totalTokenEstimate.totalTokens)} tokens totalt
                  </Badge>
                  <Badge variant="white" color="blue">
                    ca. {formatNok(totalTokenEstimate.estimatedCostNok)}
                  </Badge>
                  <Badge variant="white" color="gray">
                    søkeord {formatNumber(keywordEstimate.totalTokens)}
                  </Badge>
                  <Badge variant="white" color="gray">
                    vurdering {formatNumber(assessmentEstimate.totalTokens)}
                  </Badge>
                </Group>
              )}
            </Stack>
          </Card>
        )}

        {isLoading && (
          <Card withBorder radius="md" padding="lg">
            <Stack gap="md" align="center">
              <Loader size="xl" color="blue" />
              <Alert
                w="100%"
                color="blue"
                variant="light"
                icon={<IconSparkles size={18} />}
                title="Kjører BREEAM-sjekk"
              >
                <Stack gap={6}>
                  <Text size="sm">
                    Henter søkeord fra dokumentasjon og veiledning, velger relevante PDF-sider og lager BRE-tekst.
                  </Text>
                  <Text size="sm" c="dimmed">
                    Dette kan ta noen sekunder.
                  </Text>
                </Stack>
              </Alert>
            </Stack>
          </Card>
        )}

        {error != null && (
          <Alert color="red" variant="light" icon={<IconAlertCircle size={18} />} title="Kunne ikke hente søkeord">
            <Text size="sm">{getErrorMessage(error)}</Text>
          </Alert>
        )}

        <ScrollArea style={{ flex: 1 }}>
          {result != null ? (
            <Stack gap="md">
              {hasAiResult ? (
                <AnalysisResult result={result} />
              ) : (
                <Card withBorder radius="md" padding="md">
                  <Group justify="space-between" align="center">
                    <Text>{result.summary}</Text>
                    <Badge color="blue" variant="light" size="lg">
                      Inndata klar
                    </Badge>
                  </Group>
                </Card>
              )}
              <Card withBorder radius="md" padding="md">
                <Stack gap="sm">
                  <Group justify="space-between" align="center">
                    <Text fw={600}>Indata</Text>
                    <Button
                      variant="subtle"
                      color="gray"
                      size="xs"
                      onClick={() => setShowInputData((current) => !current)}
                    >
                      {showInputData ? "Skjul inndata" : "Vis inndata"}
                    </Button>
                  </Group>
                  {showInputData && <InputDataResult result={result} />}
                </Stack>
              </Card>
            </Stack>
          ) : hasSavedResult ? (
            <Stack gap="md">
              <SavedAnalysisResult savedResult={savedResult} />
              <Card withBorder radius="md" padding="md">
                <Stack gap="sm">
                  <Group justify="space-between" align="center">
                    <Text fw={600}>Indata</Text>
                    <Button
                      variant="subtle"
                      color="gray"
                      size="xs"
                      onClick={() => setShowInputData((current) => !current)}
                    >
                      {showInputData ? "Skjul inndata" : "Vis inndata"}
                    </Button>
                  </Group>
                  {showInputData && <SavedInputDataResult savedResult={savedResult} />}
                </Stack>
              </Card>
            </Stack>
          ) : (
            <Card withBorder radius="md" padding="lg">
              <Stack gap="xs" align="center">
                <IconSparkles size={24} color="var(--mantine-color-blue-6)" />
                <Text fw={600}>Klar til å kjøre BREEAM-sjekk</Text>
                <Text size="sm" c="dimmed" ta="center">
                  Klikk Kjør AI for søkeord, PDF-utvalg og forslag til BRE-tekst.
                </Text>
              </Stack>
            </Card>
          )}
        </ScrollArea>
      </Stack>
    </Modal>
  );
};

export default IssueBreeamCriterionCheckModal;
